import React, { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../api";
import NavBar from "../components/NavBar";
import { useLoading } from "../contexts/LoadingContext";
import InviteClientModal from "../components/InviteClientModal";
import ClientDetailsModal from "../components/ClientDetailsModal";
import ClientMatchesModal from "../components/ClientMatchesModal";
import ProfileDetailsModal from "../components/ProfileDetailsModal";
import GenderBadge from "../components/GenderBadge";
import { EyeIcon, SearchIcon, MailIcon, UsersIcon, FilterIcon } from "../components/icons";
import { ClientGridSkeleton } from "../components/Skeleton";
import { formatDate } from "../utils/dateUtils";

const ClientManagement = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inviteClient, setInviteClient] = useState(null);
  // Two distinct "open the modal" targets per client now:
  //   - detailsUserId: full demographic view (ProfileDetailsModal)
  //   - selectedClient: bureau-editable match preferences (ClientDetailsModal)
  // They used to be the same modal; splitting them keeps each surface
  // focused.
  const [detailsUserId, setDetailsUserId] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [matchesClient, setMatchesClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { setLoading: setGlobalLoading, setGlobalLoadingMessage } = useLoading();

  const fetchClients = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const data = await apiFetch("/clients");
      setClients(data.data || []);
    } catch (err) {
      console.error("Failed to fetch clients:", err);
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Pause / unpause a client without ending the bureau association.
  // Deactivating hides their matches from Matches Tracking (filtered
  // server-side); reactivating restores everything. No referrals are
  // mutated either way, so the bureau can flip-flop without losing
  // history.
  const handleToggleActive = async (client) => {
    const becomingInactive = client.isActiveClient !== false;
    const label = becomingInactive ? "deactivate" : "reactivate";
    if (
      becomingInactive &&
      !window.confirm(
        "Deactivate this client? Their matches will be hidden from Matches Tracking. You can reactivate them at any time."
      )
    ) {
      return;
    }

    try {
      setGlobalLoadingMessage(
        becomingInactive ? "Deactivating client…" : "Reactivating client…"
      );
      setGlobalLoading(true);
      await apiFetch(
        `/clients/${client._id}/${becomingInactive ? "deactivate" : "reactivate"}`,
        { method: "POST" }
      );
      fetchClients({ silent: true });
    } catch (err) {
      console.error(`Failed to ${label} client:`, err);
      alert(`Failed to ${label} client: ${err.message}`);
    } finally {
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  const handleInviteClient = async (clientId, inviteData) => {
    try {
      setGlobalLoadingMessage("Inviting client...");
      const data = await apiFetch(`/clients/${clientId}/invite`, {
        method: "POST",
        body: JSON.stringify(inviteData),
      });
      // Refresh in the background (silent — no full-page "Loading…"
      // gate) so the card flips to "invited" once the modal is
      // dismissed. The full-page gate would unmount the open modal and
      // wipe its success panel, which is exactly what we DON'T want
      // here: the bureau still needs to copy / share the credentials.
      fetchClients({ silent: true });
      return data;
    } catch (err) {
      console.error("Failed to invite client:", err);
      throw err;
    } finally {
      setGlobalLoadingMessage("");
    }
  };

  const handleUpdateClientPreferences = async (clientId, updates) => {
    try {
      setGlobalLoadingMessage("Saving client preferences...");
      setGlobalLoading(true);
      const data = await apiFetch(`/clients/${clientId}`, {
        method: "PUT",
        body: JSON.stringify(updates),
      });
      setSelectedClient(null);
      fetchClients();
      return data;
    } catch (err) {
      console.error("Failed to update client preferences:", err);
      throw err;
    } finally {
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  const filteredClients = clients.filter(client =>
    client.user?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.user?.referenceId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.user?.contactNumber?.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-background)]">
        <NavBar />
        <div className="max-w-7xl mx-auto px-4 py-6">
          <ClientGridSkeleton count={6} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--color-background)]">
        <NavBar />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-red-500">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <NavBar />

      {/* Sticky page header — stays below the NavBar while scrolling.
          z-20 keeps it above page content (including any in-card dropdowns
          at z-10) and below the mobile-menu overlay (now z-30 backdrop /
          z-50 content), so opening the hamburger still covers it. */}
      <div className="sticky top-16 z-20 bg-[var(--color-background)] border-b border-[var(--color-border)]">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 shrink-0 rounded-lg bg-[var(--color-primary-soft)] text-[var(--color-primary)] flex items-center justify-center">
              <UsersIcon className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold text-[var(--color-text-strong)] truncate">
                Client Management
              </h1>
              <p className="text-xs text-[var(--color-text-muted)]">
                Manage your clients and their profile preferences
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">

        {/* Search Bar */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search clients by name, reference ID, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] placeholder-[var(--color-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
          />
        </div>

        {/* Clients Grid */}
        {filteredClients.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-[var(--color-text-muted)] text-lg mb-2">
              {searchTerm ? "No clients found matching your search" : "No clients yet"}
            </div>
            <div className="text-[var(--color-text-muted)]">
              {searchTerm ? "Try adjusting your search terms" : "Add your first client to get started"}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClients.map((client) => (
              <div
                key={client._id}
                className={`bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 hover:shadow-md transition-shadow ${
                  client.isActiveClient === false ? "opacity-70" : ""
                }`}
              >
                {/* Card body mirrors the Dashboard / FindMatches
                    compact card so the bureau gets the same at-a-glance
                    profile picture whether they're browsing all
                    profiles or looking at their client roster:
                    ID → name + gender badge → demographic rows. */}
                <div className="flex justify-between items-start mb-1">
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-0.5">
                      ID {client.user?.referenceId || "—"}
                    </div>
                  </div>
                  {/* Inactive badge replaces the old ✕ remove button.
                      Deactivate / Reactivate live as a dedicated button
                      at the bottom of the action stack — keeping a
                      destructive-looking ✕ in the corner risked
                      conditioning the bureau to a one-tap action when
                      this is now a reversible pause, not a delete. */}
                  {client.isActiveClient === false && (
                    <span
                      className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gray-200 text-gray-700"
                      title={
                        client.clientDeactivatedAt
                          ? `Deactivated ${formatDate(client.clientDeactivatedAt)}`
                          : "Deactivated"
                      }
                    >
                      Inactive
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mb-3 pr-2">
                  {/* Name is omitted entirely when missing (the ID line
                      above already identifies the row). The gender
                      badge still renders so the bureau can see at a
                      glance which gender this client is — useful when
                      the card has no name yet. */}
                  {client.user?.fullName && (
                    <h3 className="text-base font-semibold text-[var(--color-text-strong)] truncate">
                      {client.user.fullName}
                    </h3>
                  )}
                  <GenderBadge gender={client.user?.gender} />
                </div>

                <div className="space-y-1 text-xs">
                  <div className="flex justify-between gap-2">
                    <span className="text-[var(--color-text-muted)]">Age</span>
                    <span className="text-[var(--color-text)] font-medium">
                      {client.user?.age ?? "—"}
                    </span>
                  </div>
                  {(client.heightFeet || client.heightInches) && (
                    <div className="flex justify-between gap-2">
                      <span className="text-[var(--color-text-muted)]">Height</span>
                      <span className="text-[var(--color-text)] font-medium">
                        {client.heightFeet ?? "—"}ft {client.heightInches ?? 0}in
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between gap-2">
                    <span className="text-[var(--color-text-muted)]">City</span>
                    <span className="text-[var(--color-text)] font-medium truncate">
                      {client.user?.city || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-[var(--color-text-muted)]">Profession</span>
                    <span className="text-[var(--color-text)] font-medium truncate">
                      {client.user?.profession || "—"}
                    </span>
                  </div>
                  {(client.user?.cast || client.caste) && (
                    <div className="flex justify-between gap-2">
                      <span className="text-[var(--color-text-muted)]">Caste</span>
                      <span className="text-[var(--color-text)] font-medium truncate">
                        {client.user?.cast || client.caste}
                      </span>
                    </div>
                  )}
                  {client.user?.maritalStatus && (
                    <div className="flex justify-between gap-2">
                      <span className="text-[var(--color-text-muted)]">Marital</span>
                      <span className="text-[var(--color-text)] font-medium capitalize">
                        {client.user.maritalStatus}
                      </span>
                    </div>
                  )}
                  {client.user?.contactNumber && (
                    <div className="flex justify-between gap-2">
                      <span className="text-[var(--color-text-muted)]">Phone</span>
                      <span className="text-[var(--color-text)] font-medium truncate">
                        {client.user.contactNumber}
                      </span>
                    </div>
                  )}
                </div>

                {/* Footer line — added date + views, same shape as the
                    Dashboard card so the bureau's eye lands in the same
                    place across surfaces. */}
                <div className="mt-3 pt-3 border-t border-[var(--color-border)] flex items-center justify-between gap-2 text-[10px] text-[var(--color-text-muted)]">
                  <span className="truncate">
                    Added {client.createdAt ? formatDate(client.createdAt) : "—"}
                  </span>
                  <span className="inline-flex items-center gap-1 shrink-0">
                    <svg
                      viewBox="0 0 24 24"
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    {client.views ?? 0}
                  </span>
                </div>

                {/* Statistics */}
                <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-base font-bold text-[var(--color-primary)]">
                        {client.totalReferrals || 0}
                      </div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mt-0.5">Total</div>
                    </div>
                    <div>
                      <div className="text-base font-bold text-blue-500">
                        {client.activeReferrals || 0}
                      </div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mt-0.5">Active</div>
                    </div>
                    <div>
                      <div className="text-base font-bold text-green-500">
                        {client.successfulMatches || 0}
                      </div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mt-0.5">Success</div>
                    </div>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
                    Login Email
                  </div>
                  <div className="text-xs text-[var(--color-text)] mb-3 truncate">
                    {client.hasLogin
                      ? client.email || client.user?.email || "—"
                      : "Not invited"}
                  </div>
                  <div className="space-y-2">
                    {/* Order matches the bureau's typical workflow:
                        invite first (so the client can log in), queue
                        matches, then drill into details / preferences
                        as needed. All four use the same neutral style;
                        the card is a navigation surface, not a place
                        to surface one "primary" action. */}
                    <button
                      onClick={() => setInviteClient(client)}
                      className="w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] transition-colors text-sm font-medium"
                    >
                      <MailIcon className="w-4 h-4" />
                      {client.hasLogin ? "Reset Password" : "Invite Client"}
                    </button>
                    <button
                      onClick={() => setMatchesClient(client)}
                      className="w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] transition-colors text-sm font-medium"
                    >
                      <SearchIcon className="w-4 h-4" />
                      Find Matches
                    </button>
                    <button
                      onClick={() => setDetailsUserId(client._id)}
                      className="w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] transition-colors text-sm font-medium"
                    >
                      <EyeIcon className="w-4 h-4" />
                      Show Details
                    </button>
                    <button
                      onClick={() => setSelectedClient(client)}
                      className="w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] transition-colors text-sm font-medium"
                    >
                      <FilterIcon className="w-4 h-4" />
                      Show Preferences
                    </button>
                    {/* Deactivate / Reactivate. Deactivate styled in
                        red (warning, not destructive) so the bureau
                        registers the consequence — matches are hidden
                        for this client — without thinking it's a hard
                        delete. Reactivate uses the primary theme color
                        since restoring is the desired state.
                        Confirmation prompt happens inside the handler
                        only on deactivate, not on reactivate. */}
                    {client.isActiveClient === false ? (
                      <button
                        onClick={() => handleToggleActive(client)}
                        className="w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] transition-colors text-sm font-semibold"
                      >
                        Reactivate
                      </button>
                    ) : (
                      <button
                        onClick={() => handleToggleActive(client)}
                        className="w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 transition-colors text-sm font-medium"
                      >
                        Deactivate
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <InviteClientModal
        isOpen={Boolean(inviteClient)}
        client={inviteClient}
        onClose={() => setInviteClient(null)}
        onInvite={handleInviteClient}
      />
      <ClientDetailsModal
        isOpen={Boolean(selectedClient)}
        client={selectedClient}
        onClose={() => setSelectedClient(null)}
        onUpdateClient={handleUpdateClientPreferences}
      />
      <ClientMatchesModal
        isOpen={Boolean(matchesClient)}
        client={matchesClient}
        onClose={() => setMatchesClient(null)}
        onReferralCreated={fetchClients}
      />
      <ProfileDetailsModal
        isOpen={Boolean(detailsUserId)}
        userId={detailsUserId}
        onClose={() => setDetailsUserId(null)}
      />
    </div>
  );
};

export default ClientManagement;